import { getFormInputs, postTicket } from "@/api/ticket";
import { useLoadingStore } from "@/stores/loading";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

export type FormItem = {
  id: number;
  name: string;
  column_name: string;
  attribute_options: {
    id: number;
    attribute_id: number;
    code: string;
    option: string;
    attribute_option_parent_values?: {
      id: number;
      attribute_option_id: number;
      parent_attribute_options_id: number | null;
      parent_area_id?: number | null;
      parent_block_id?: number | null;
    }[];
  }[];
};

// 定数
const VALIDATION_RULES = {
  INQUIRY_TITLE_MAX_LENGTH: 200,
  INQUIRY_DETAIL_MAX_LENGTH: 500,
  REQUIRED_MESSAGE: "この項目は必須です",
};

const COLUMN_NAMES = {
  INQUIRY_TYPE1: "inquiry_type1",
  INQUIRY_TYPE2: "inquiry_type2",
  INQUIRY_TITLE: "inquiry_title",
  INQUIRY_DETAIL: "inquiry_detail",
} as const;

// ヘルパー関数
const getValidationRules = (columnName: string) => {
  if (
    columnName === COLUMN_NAMES.INQUIRY_TITLE ||
    columnName === COLUMN_NAMES.INQUIRY_DETAIL
  ) {
    const maxLength =
      columnName === COLUMN_NAMES.INQUIRY_TITLE
        ? VALIDATION_RULES.INQUIRY_TITLE_MAX_LENGTH
        : VALIDATION_RULES.INQUIRY_DETAIL_MAX_LENGTH;
    return {
      required: VALIDATION_RULES.REQUIRED_MESSAGE,
      maxLength: {
        value: maxLength,
        message: `${maxLength}文字以内で入力してください`,
      },
    };
  }
  return { required: VALIDATION_RULES.REQUIRED_MESSAGE };
};

const encodeFiles = async (files: File[]) => {
  return Promise.all(
    files.map(async (file) => {
      return new Promise<{
        original_filename: string;
        file_content: string;
      }>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result;
          resolve({
            original_filename: file.name,
            file_content: String(base64),
          });
        };
        reader.readAsDataURL(file);
      });
    })
  );
};

export const useTicketForm = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    register,
    getValues,
    formState: { errors },
    trigger,
    watch,
    setValue,
  } = useForm();
  const [formInputs, setFormInputs] = useState<FormItem[] | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const setLoading = useLoadingStore((state) => state.setLoading);
  const navigate = useNavigate();

  // フォームデータ取得
  useEffect(() => {
    if (formInputs) return;
    setLoading(true);
    getFormInputs()
      .then((result) => {
        setFormInputs(result.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // 問合せ区分ロジック
  const inquiryType1 = formInputs?.find(
    (item) => item.column_name === COLUMN_NAMES.INQUIRY_TYPE1
  );
  const inquiryType2 = formInputs?.find(
    (item) => item.column_name === COLUMN_NAMES.INQUIRY_TYPE2
  );

  const watchedType1 = watch(inquiryType1?.column_name || "");

  const selectedType1Option = inquiryType1?.attribute_options.find(
    (opt) => opt.code === watchedType1
  );

  const selectedType1Id = selectedType1Option?.id;
  const selectedType1OptionLabel = selectedType1Option?.option;
  const isType1Other = selectedType1OptionLabel === "その他";

  const type2OtherOption = inquiryType2?.attribute_options.find(
    (opt) => opt.option === "その他"
  );

  useEffect(() => {
    if (isType1Other && type2OtherOption && inquiryType2) {
      setValue(inquiryType2.column_name, type2OtherOption.code);
    }
  }, [isType1Other, type2OtherOption, inquiryType2, setValue]);

  const filteredType2Options =
    inquiryType2 && selectedType1Id && !isType1Other
      ? inquiryType2.attribute_options.filter((opt) =>
          opt.attribute_option_parent_values?.some(
            (parent) => parent.parent_attribute_options_id === selectedType1Id
          )
        )
      : inquiryType2?.attribute_options || [];

  // その他のフォーム項目
  const otherFormItems =
    formInputs?.filter(
      (item) =>
        item.column_name !== COLUMN_NAMES.INQUIRY_TYPE1 &&
        item.column_name !== COLUMN_NAMES.INQUIRY_TYPE2
    ) || [];

  // イベントハンドラー
  const handleConfirmButtonClick = async () => {
    const isValid = await trigger();
    if (isValid) {
      setIsConfirmModalOpen(true);
    }
  };

  const handleConfirmSubmit = async () => {
    setIsConfirmModalOpen(false);
    setLoading(true);

    try {
      const formValues = getValues();
      const files = fileInputRef.current?.files;
      const fileArray = files ? Array.from(files) : [];

      const encodedFiles = await encodeFiles(fileArray);

      const submitData = {
        ...formValues,
        files: encodedFiles,
      };
      console.log(submitData);

      await postTicket(submitData);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      navigate("/ticket");
    } catch (error) {
      console.error("送信エラー:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubmit = () => {
    setIsConfirmModalOpen(false);
  };

  return {
    fileInputRef,
    register,
    errors,
    formInputs,
    isConfirmModalOpen,
    inquiryType1,
    inquiryType2,
    isType1Other,
    filteredType2Options,
    otherFormItems,
    handleConfirmButtonClick,
    handleConfirmSubmit,
    handleCancelSubmit,
    getValidationRules,
  };
};
